import React, { useState, useEffect } from 'react';
import { useStore } from '../lib/store';
import { Users, Target, BookOpen, MessageCircle, Save, Check } from 'lucide-react';

export const TeachingContext = () => {
  const { teachingContext, setTeachingContext } = useStore();
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [localContext, setLocalContext] = useState(teachingContext);

  // Update local state when teachingContext changes (e.g., on load)
  useEffect(() => {
    setLocalContext(teachingContext);
  }, [teachingContext]);

  const handleContextChange = (field: keyof typeof teachingContext, value: string) => {
    setLocalContext(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setTeachingContext(localContext);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving context:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-amber-500 flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Teaching Context
          </h2>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50"
        >
          {showSaveSuccess ? (
            <Check className="w-5 h-5 text-white" />
          ) : (
            <Save className={`w-5 h-5 text-white ${isSaving ? 'animate-spin' : ''}`} />
          )}
          <span>{showSaveSuccess ? 'Saved!' : (isSaving ? 'Saving...' : 'Save Context')}</span>
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <label className="block">
            <div className="flex items-center gap-2 text-gray-300 mb-2">
              <Users className="w-4 h-4 text-amber-500" />
              <span>Target Audience</span>
            </div>
            <textarea
              className="w-full h-24 bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              placeholder="Describe your target audience (e.g., beginners, professionals, students)"
              value={localContext.audience}
              onChange={(e) => handleContextChange('audience', e.target.value)}
            />
          </label>

          <label className="block">
            <div className="flex items-center gap-2 text-gray-300 mb-2">
              <Target className="w-4 h-4 text-amber-500" />
              <span>Learning Objectives</span>
            </div>
            <textarea
              className="w-full h-24 bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              placeholder="What should the audience learn from this teaching?"
              value={localContext.objectives}
              onChange={(e) => handleContextChange('objectives', e.target.value)}
            />
          </label>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="flex items-center gap-2 text-gray-300 mb-2">
              <BookOpen className="w-4 h-4 text-amber-500" />
              <span>Prior Knowledge</span>
            </div>
            <textarea
              className="w-full h-24 bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              placeholder="What should the audience already know?"
              value={localContext.priorKnowledge}
              onChange={(e) => handleContextChange('priorKnowledge', e.target.value)}
            />
          </label>

          <label className="block">
            <div className="flex items-center gap-2 text-gray-300 mb-2">
              <MessageCircle className="w-4 h-4 text-amber-500" />
              <span>Additional Context</span>
            </div>
            <textarea
              className="w-full h-24 bg-gray-950 border border-gray-800 rounded-lg p-3 text-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              placeholder="Any additional context or notes for the AI"
              value={localContext.additionalContext}
              onChange={(e) => handleContextChange('additionalContext', e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
};